import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Network, Bot } from 'lucide-react';

interface SavvyDataProposalCardProps {
  onClick: () => void;
}

const CardContainer = styled(motion(Box))(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
    border: '1px solid rgba(0, 255, 255, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  }
}));

const FeatureList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      color: '#00FFFF',
    }
  }
}));

const SavvyDataProposalCard: React.FC<SavvyDataProposalCardProps> = ({ onClick }) => {
  return (
    <CardContainer
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: '#00FFFF' }}>
          SavvyData Marketing Platform
        </Typography>
        <Chip 
          label="Interactive Demo" 
          size="small"
          sx={{ 
            backgroundColor: 'rgba(138, 43, 226, 0.2)',
            color: '#FFFFFF',
            borderRadius: '4px'
          }}
        />
      </Box>
      
      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
        Leverage AI to identify, engage, and convert high-value B2B prospects with our 
        intelligent sales and marketing platform.
      </Typography>
      
      <FeatureList>
        <Box>
          <Bot size={18} />
          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
            AI-powered prospect research and enrichment
          </Typography>
        </Box>
        <Box>
          <Network size={18} />
          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
            Deep buyer persona analysis and engagement tracking
          </Typography>
        </Box>
        <Box>
          <Bot size={18} />
          <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
            Personalized communication templates and recommendations
          </Typography>
        </Box>
      </FeatureList>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Chip 
          label="$45,000" 
          size="medium"
          sx={{ 
            backgroundColor: 'rgba(0, 204, 153, 0.2)',
            color: '#00CC99',
            borderRadius: '4px',
            fontWeight: 'bold'
          }}
        />
      </Box>
    </CardContainer>
  );
};

export default SavvyDataProposalCard; 